import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from "firebase/firestore";
import { FileText, Clock, CheckCircle, X, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { useMemo } from 'react';

const AccountsOrders = ({ db, user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    customerName: '',
  });

  useEffect(() => {
    setLoading(true);
    // Listen to orders
    const unsubscribeOrders = onSnapshot(collection(db, "orders"), (querySnapshot) => {
      const ordersList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setOrders(ordersList);
    });

    // Listen to dispatches for DC status
    const unsubscribeDispatches = onSnapshot(collection(db, "dispatches"), (querySnapshot) => {
      const dispatchList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setDispatches(dispatchList);
      setLoading(false);
    });

    return () => {
        unsubscribeOrders();
        unsubscribeDispatches();
    };
  }, [db]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

    const filteredAndSorted = useMemo(() => {
      // For Accounts, we primarily show orders that have reached dispatch stage
      // or are relevant for invoicing.
      let list = dispatches.filter(d => {
        // Only show if it's pending invoice or ready
        const status = d.dispatchStatus;
        if (!['invoice_pending', 'invoice_ready', 'dc_confirmed', 'qc_passed', 'dispatched', 'in_transit', 'delivered'].includes(status)) return false;

        if (filters.status && status !== filters.status) return false;
        if (filters.customerName && d.customerName?.toLowerCase().indexOf(filters.customerName.toLowerCase()) === -1) return false;
        
        if (filters.startDate || filters.endDate) {
          const date = new Date(d.updatedAt?.toDate ? d.updatedAt.toDate() : d.updatedAt);
          date.setHours(0,0,0,0);
          if (filters.startDate && date < new Date(filters.startDate)) return false;
          if (filters.endDate && date > new Date(filters.endDate)) return false;
        }
        return true;
      });

      if (sortColumn) {
        list.sort((a, b) => {
          let aVal = a[sortColumn];
          let bVal = b[sortColumn];

          if (sortColumn === 'updatedAt') {
              aVal = aVal?.toDate ? aVal.toDate() : new Date(aVal);
              bVal = bVal?.toDate ? bVal.toDate() : new Date(bVal);
          }

          if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
          return aVal < bVal ? 1 : -1;
        });
      }
      return list;
    }, [dispatches, filters, sortColumn, sortDirection]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSorted, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);

  const renderStatus = (status) => {
    switch (status) {
      case 'packed_ready':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}><CheckCircle size={14} /> Packing Done</span>;
      case 'invoice_pending':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}><Clock size={14} /> Invoice Pending</span>;
      case 'invoice_ready':
      case 'dc_confirmed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}><CheckCircle size={14} /> Invoice Ready</span>;
      case 'qc_passed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}><CheckCircle size={14} /> QC Passed</span>;
      case 'dispatched':
        return <span style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '5px' }}><Truck size={14} /> Dispatched</span>;
      case 'delivered':
        return <span style={{ color: '#059669', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Delivered</span>;
      default:
        return <span style={{ color: '#64748b' }}>{status?.replace(/_/g, ' ').toUpperCase()}</span>;
    }
  };

  const formatDate = (date) => {
      if (!date) return '--';
      const d = date?.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px' }}>Dispatch & Invoicing Queue ({filteredAndSorted.length})</h2>

      {/* Filter Section */}
      <div style={filterSectionStyle}>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All Statuses</option>
            <option value="invoice_pending">Invoice Pending</option>
            <option value="invoice_ready">Invoice Ready</option>
            <option value="dispatched">Dispatched</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              <th style={thStyle}>Last Updated</th>
              <th style={{...thStyle, textAlign:'left'}}>Customer / Project</th>
              <th style={thStyle}>MOC No.</th>
              <th style={thStyle}>DC No.</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((item) => (
              <tr 
                key={item.id} 
                style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                onClick={() => navigate(`/ops/dispatch/${item.id}`)}
                className="hover-highlight"
              >
                <td style={tdStyle}>{formatDate(item.updatedAt)}</td>
                <td style={{...tdStyle, textAlign:'left'}}>
                  <div style={{fontWeight: 'bold', fontSize: '14px'}}>{item.customerName}</div>
                  <div style={{fontSize: '11px', color: '#6b7280'}}>{item.warehouse} Warehouse</div>
                </td>
                <td style={tdStyle}>{item.mocNo}</td>
                <td style={tdStyle}>{item.challanNo || '--'}</td>
                <td style={tdStyle}>{renderStatus(item.dispatchStatus)}</td>
                <td style={tdStyle}>
                  <button 
                    style={{ 
                        padding: '6px 12px', borderRadius: '6px', border: '1px solid #2563eb', 
                        color: '#2563eb', backgroundColor: 'transparent', fontWeight: 'bold', fontSize: '12px',
                        cursor: 'pointer'
                    }}
                  >
                    {item.dispatchStatus === 'invoice_pending' ? 'Upload Invoice' : 'View DC'}
                  </button>
                </td>
              </tr>
            ))}
            {filteredAndSorted.length === 0 && (
                <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                        No dispatch records matching your filters.
                    </td>
                </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
            <div style={{ padding: '15px', display: 'flex', justifyContent: 'center', gap: '10px', borderTop: '1px solid #e5e7eb' }}>
                <button 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => p - 1)}
                    style={paginationButtonStyle}
                >Previous</button>
                <span style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>Page {currentPage} of {totalPages}</span>
                <button 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => p + 1)}
                    style={paginationButtonStyle}
                >Next</button>
            </div>
        )}
      </div>
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: '150px' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' };
const paginationButtonStyle = { padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer' };

export default AccountsOrders;
